import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  MenuItem,
  Grid,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Alert,
  CircularProgress
} from '@mui/material';
import { 
  Add as AddIcon, 
  FileDownload as ExportIcon,
  FileUpload as ImportIcon,
  ArrowBack as ArrowBackIcon 
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Currency } from '../utils/currency';
import { apiService } from '../services/apiService';

function Transactions() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [filterPeriod, setFilterPeriod] = useState('2024-01');
  const [error, setError] = useState(null);

  // New transaction form state
  const [newTransaction, setNewTransaction] = useState({
    date: new Date().toISOString().split('T')[0],
    type: 'sale',
    customer_vendor: '',
    vat_number: '',
    amount_excl_vat: '',
    vat_rate: 20,
    vat_amount: 0,
    total_amount: 0,
    category: 'Services',
    status: 'unpaid'
  });

  // Check permissions
  const canCreate = user?.permissions?.transactions?.includes('create');
  const canExport = user?.permissions?.transactions?.includes('export');
  const canImport = user?.permissions?.transactions?.includes('import');

  // Load transactions from API
  const loadTransactions = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiService.getTransactions();
      if (result.success) {
        setTransactions(result.data);
      } else {
        setError(result.error || 'Failed to load transactions');
      }
    } catch (error) {
      setError(error.message || 'Failed to load transactions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTransactions();
  }, []);

  // Calculate VAT when amount or rate changes
  useEffect(() => {
    const amount = parseFloat(newTransaction.amount_excl_vat) || 0;
    const vatRate = parseFloat(newTransaction.vat_rate) || 0;
    const vatAmount = amount * (vatRate / 100);
    const totalAmount = amount + vatAmount;

    setNewTransaction(prev => ({
      ...prev,
      vat_amount: Math.round(vatAmount * 100) / 100,
      total_amount: Math.round(totalAmount * 100) / 100
    }));
  }, [newTransaction.amount_excl_vat, newTransaction.vat_rate]);

  // Real export functionality
  const handleExport = async () => {
    try {
      const result = await apiService.exportTransactions();
      if (result.success) {
        // Convert to CSV and download
        const csv = convertToCSV(result.data);
        downloadCSV(csv, 'transactions.csv');
      } else {
        alert('Export failed: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      alert('Export failed: ' + error.message);
    }
  };

  const handleImport = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Implement CSV import logic
      alert('CSV import will be implemented in the next version');
      // Reset file input
      event.target.value = '';
    }
  };

  // Convert data to CSV format
  const convertToCSV = (data) => {
    if (!data || data.length === 0) return '';
    
    const headers = Object.keys(data[0]);
    const csvHeaders = headers.join(',');
    const csvRows = data.map(row => 
      headers.map(header => {
        const value = row[header] || '';
        // Handle values that might contain commas
        return `"${String(value).replace(/"/g, '""')}"`;
      }).join(',')
    );
    
    return [csvHeaders, ...csvRows].join('\n');
  };

  // Download CSV file
  const downloadCSV = (csv, filename) => {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleAddTransaction = async () => {
    if (!newTransaction.customer_vendor || !newTransaction.amount_excl_vat) {
      alert('Please fill in required fields');
      return;
    }

    try {
      const transactionToAdd = {
        ...newTransaction,
        amount_excl_vat: parseFloat(newTransaction.amount_excl_vat),
        created_by: user.email
      };

      const result = await apiService.addTransaction(transactionToAdd);
      
      if (result.success) {
        alert('Transaction added successfully!');
        setShowAddForm(false);
        setNewTransaction({
          date: new Date().toISOString().split('T')[0],
          type: 'sale',
          customer_vendor: '',
          vat_number: '',
          amount_excl_vat: '',
          vat_rate: 20,
          vat_amount: 0,
          total_amount: 0,
          category: 'Services',
          status: 'unpaid'
        });
        loadTransactions(); // Reload transactions
      } else {
        alert('Failed to add transaction: ' + result.error);
      }
    } catch (error) {
      alert('Error adding transaction: ' + error.message);
    }
  };

  const totalVAT = transactions.reduce((sum, t) => sum + (parseFloat(t.vat_amount) || 0), 0);
  const totalSales = transactions
    .filter(t => t.type === 'sale')
    .reduce((sum, t) => sum + (parseFloat(t.amount_excl_vat) || 0), 0);
  const totalPurchases = transactions
    .filter(t => t.type === 'purchase')
    .reduce((sum, t) => sum + (parseFloat(t.amount_excl_vat) || 0), 0);

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton 
            onClick={() => navigate('/dashboard')}
            color="primary"
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4">Transactions</Typography>
        </Box>
        <Box display="flex" gap={2}>
          {canImport && (
            <>
              <input
                accept=".csv"
                style={{ display: 'none' }}
                id="import-file"
                type="file"
                onChange={handleImport}
              />
              <label htmlFor="import-file">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<ImportIcon />}
                >
                  Import
                </Button>
              </label>
            </>
          )}
          {canExport && (
            <Button
              variant="outlined"
              startIcon={<ExportIcon />}
              onClick={handleExport}
              disabled={loading}
            >
              Export
            </Button>
          )}
          {canCreate && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setShowAddForm(true)}
            >
              Add Transaction
            </Button>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" style={{ marginBottom: '20px' }}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Transactions
              </Typography>
              <Typography variant="h5">
                {loading ? '...' : transactions.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total VAT
              </Typography>
              <Typography variant="h5" color="primary">
                {loading ? '...' : Currency.format(totalVAT)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Sales
              </Typography>
              <Typography variant="h5" style={{ color: '#2e7d32' }}>
                {loading ? '...' : Currency.format(totalSales)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Purchases
              </Typography>
              <Typography variant="h5" style={{ color: '#ed6c02' }}>
                {loading ? '...' : Currency.format(totalPurchases)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper style={{ padding: '20px', marginBottom: '20px' }}>
        <Typography variant="h6" gutterBottom>Filters</Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              fullWidth
              label="Period"
              value={filterPeriod}
              onChange={(e) => setFilterPeriod(e.target.value)}
              size="small"
            >
              <MenuItem value="2024-01">January 2024</MenuItem>
              <MenuItem value="2024-02">February 2024</MenuItem>
              <MenuItem value="2024-03">March 2024</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              fullWidth
              label="Transaction Type"
              defaultValue="all"
              size="small"
            >
              <MenuItem value="all">All Types</MenuItem>
              <MenuItem value="sale">Sales</MenuItem>
              <MenuItem value="purchase">Purchases</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button 
              variant="outlined" 
              onClick={loadTransactions}
              disabled={loading}
            >
              {loading ? <CircularProgress size={20} /> : 'Refresh'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Transactions List */}
      <Paper>
        <Box p={2}>
          <Typography variant="h6" gutterBottom>
            Recent Transactions {loading && <CircularProgress size={16} style={{ marginLeft: '10px' }} />}
          </Typography>
          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : transactions.length === 0 ? (
            <Box textAlign="center" p={3}>
              <Typography color="textSecondary">
                No transactions found. {canCreate && 'Add your first transaction!'}
              </Typography>
            </Box>
          ) : (
            transactions.map((transaction, index) => (
              <Card key={transaction.transaction_id || index} variant="outlined" style={{ marginBottom: '10px' }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="h6">
                        {transaction.customer_vendor}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {transaction.date} • {transaction.category}
                        {transaction.vat_number && ` • VAT: ${transaction.vat_number}`}
                      </Typography>
                    </Box>
                    <Box textAlign="right">
                      <Typography variant="h6">
                        {Currency.format(parseFloat(transaction.total_amount) || 0)}
                      </Typography>
                      <Box display="flex" gap={1} justifyContent="flex-end">
                        <Chip 
                          label={transaction.type} 
                          size="small"
                          color={transaction.type === 'sale' ? 'success' : 'warning'}
                          variant="outlined"
                        />
                        <Chip 
                          label={transaction.status} 
                          size="small"
                          color={transaction.status === 'paid' ? 'success' : 'default'}
                        />
                      </Box>
                    </Box>
                  </Box>
                  <Typography variant="body2" style={{ marginTop: '8px' }}>
                    Amount: {Currency.format(parseFloat(transaction.amount_excl_vat) || 0)} + 
                    VAT ({transaction.vat_rate}%): {Currency.format(parseFloat(transaction.vat_amount) || 0)}
                  </Typography>
                </CardContent>
              </Card>
            ))
          )}
        </Box>
      </Paper>

      {/* Add Transaction Dialog */}
      <Dialog 
        open={showAddForm} 
        onClose={() => setShowAddForm(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add New Transaction</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} style={{ marginTop: '10px' }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Date"
                type="date"
                value={newTransaction.date}
                onChange={(e) => setNewTransaction({...newTransaction, date: e.target.value})}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={newTransaction.type}
                  label="Type"
                  onChange={(e) => setNewTransaction({...newTransaction, type: e.target.value})}
                >
                  <MenuItem value="sale">Sale</MenuItem>
                  <MenuItem value="purchase">Purchase</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Customer/Vendor"
                value={newTransaction.customer_vendor}
                onChange={(e) => setNewTransaction({...newTransaction, customer_vendor: e.target.value})}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="VAT Number"
                value={newTransaction.vat_number}
                onChange={(e) => setNewTransaction({...newTransaction, vat_number: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Amount (excl. VAT)"
                type="number"
                value={newTransaction.amount_excl_vat}
                onChange={(e) => setNewTransaction({...newTransaction, amount_excl_vat: e.target.value})}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="VAT Rate %"
                type="number"
                value={newTransaction.vat_rate}
                onChange={(e) => setNewTransaction({...newTransaction, vat_rate: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="VAT Amount"
                type="number"
                value={newTransaction.vat_amount}
                disabled
                helperText="Calculated automatically"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Total Amount"
                type="number"
                value={newTransaction.total_amount}
                disabled
                helperText="Calculated automatically"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Category"
                value={newTransaction.category}
                onChange={(e) => setNewTransaction({...newTransaction, category: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={newTransaction.status}
                  label="Status"
                  onChange={(e) => setNewTransaction({...newTransaction, status: e.target.value})}
                >
                  <MenuItem value="unpaid">Unpaid</MenuItem>
                  <MenuItem value="paid">Paid</MenuItem>
                  <MenuItem value="partial">Partial</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddForm(false)}>Cancel</Button>
          <Button 
            onClick={handleAddTransaction} 
            variant="contained"
            disabled={!newTransaction.customer_vendor || !newTransaction.amount_excl_vat}
          >
            Add Transaction
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Transactions;